/**
 * AISuggestPanel — Bedrock Claude-powered field suggestions for the
 * Primitive Field Constellation visualization.
 *
 * Sends the user's goal + primitive context to the `suggestFields` mutation
 * and renders ranked field cards with relevance scores and reasoning.
 */

import React, { useState, useCallback } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconSparkles,
  IconX,
  IconArrowUp,
} from '@tabler/icons-react';

export interface FieldSuggestionItem {
  fieldName: string;
  ringId: string;
  reasoning: string;
  relevance: number;
  critical: boolean;
}

export interface AISuggestPanelProps {
  /** Current primitive type (e.g., "Task") */
  primitiveType: string;
  /** Active context profile ID (if any) */
  contextId: string | null;
  /** Called when user wants to highlight a suggested field on the graph */
  onHighlightField?: (fieldName: string) => void;
  /** Mutation function — injected from generated hook */
  onSuggest: (input: {
    primitiveType: string;
    contextId?: string | null;
    userGoal: string;
    vertical?: string | null;
  }) => Promise<{
    suggestions: FieldSuggestionItem[];
    provider: string;
    totalFieldsConsidered: number;
  }>;
}

export function AISuggestPanel({
  primitiveType,
  contextId,
  onHighlightField,
  onSuggest,
}: AISuggestPanelProps) {
  const [goal, setGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<FieldSuggestionItem[]>([]);
  const [provider, setProvider] = useState<string | null>(null);
  const [totalFields, setTotalFields] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const handleSuggest = useCallback(async () => {
    if (!goal.trim()) return;
    setLoading(true);
    setError(null);
    setSuggestions([]);
    try {
      const result = await onSuggest({
        primitiveType,
        contextId,
        userGoal: goal.trim(),
      });
      setSuggestions(result.suggestions);
      setProvider(result.provider);
      setTotalFields(result.totalFieldsConsidered);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to get suggestions');
    } finally {
      setLoading(false);
    }
  }, [goal, primitiveType, contextId, onSuggest]);

  if (!panelOpen) {
    return (
      <Tooltip label="AI Field Suggestions" position="left">
        <ActionIcon
          variant="gradient"
          gradient={{ from: 'violet', to: 'grape' }}
          size="lg"
          radius="xl"
          onClick={() => setPanelOpen(true)}
        >
          <IconSparkles size={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Paper withBorder p="sm" w={340} style={{ flexShrink: 0 }}>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group gap={6}>
            <IconSparkles size={16} color="var(--mantine-color-violet-6)" />
            <Text fw={600} size="sm">
              AI Suggest
            </Text>
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setPanelOpen(false)}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>

        {/* Goal input */}
        <Textarea
          placeholder="Describe what you're trying to accomplish..."
          value={goal}
          onChange={(e) => setGoal(e.currentTarget.value)}
          minRows={2}
          maxRows={4}
          size="xs"
          autosize
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSuggest();
            }
          }}
        />

        <Button
          leftSection={loading ? <Loader size={14} /> : <IconArrowUp size={14} />}
          onClick={handleSuggest}
          disabled={!goal.trim() || loading}
          size="xs"
          variant="gradient"
          gradient={{ from: 'violet', to: 'grape' }}
          fullWidth
        >
          {loading ? 'Analyzing...' : 'Suggest Fields'}
        </Button>

        {/* Error */}
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}

        {/* Results */}
        {suggestions.length > 0 && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {suggestions.length} of {totalFields} fields suggested
              </Text>
              {provider && (
                <Badge size="xs" variant="dot" color="violet">
                  {provider}
                </Badge>
              )}
            </Group>

            {suggestions.map((s) => (
              <Card
                key={s.fieldName}
                padding="xs"
                withBorder
                style={{
                  cursor: onHighlightField ? 'pointer' : 'default',
                  borderLeftWidth: 3,
                  borderLeftColor: s.critical
                    ? 'var(--mantine-color-red-6)'
                    : 'var(--mantine-color-violet-4)',
                }}
                onClick={() => onHighlightField?.(s.fieldName)}
              >
                <Group justify="space-between" wrap="nowrap" gap={4}>
                  <Text size="xs" fw={600} truncate>
                    {s.fieldName}
                  </Text>
                  <Group gap={4} wrap="nowrap">
                    {s.critical && (
                      <Badge size="xs" color="red" variant="filled">
                        Critical
                      </Badge>
                    )}
                    <Badge size="xs" color="violet" variant="light">
                      {s.ringId}
                    </Badge>
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mt={2}>
                  {s.reasoning}
                </Text>
                <Progress
                  value={s.relevance * 100}
                  size="xs"
                  color="violet"
                  mt={4}
                />
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
